import clsx from "clsx";
import type { ComponentProps } from "react";

import { ButtonWithIcon } from "@shared/ui/button-with-icon";
import { Body, Heading } from "@shared/ui/typography";

import type { PricingPlansType } from "../../model";

import s from "./pricing-plans.module.scss";

export type CompProps = ComponentProps<"div"> & {
  tables: PricingPlansType[];
  moreBtn?: any;
};

export const PricingPlans = (props: CompProps) => {
  const { tables, moreBtn } = props;

  return (
    <section className={clsx(s.root)} id="pricing-plans">
      <div className={s.tables}>
        {tables?.length > 0 &&
          tables.map((table, tableIdx) => {
            const style: React.CSSProperties & { ["--columns"]: string } = {
              "--columns": table?.columnTitles?.length?.toString() ?? "2",
            };

            const isSmall = (table?.columnTitles?.length ?? 0) < 3;
            const periods = table?.period?.split(/( — )/) ?? [];

            return (
              <div
                className={s.table}
                style={style}
                key={table?.title ?? tableIdx}
              >
                {table?.title && (
                  <Heading level={6} tag="h2" className={s.title}>
                    <span>{table.title}</span>
                    {periods?.length > 0 && (
                      <span>
                        {periods.map((period) => (
                          <span key={period}>{period}</span>
                        ))}
                      </span>
                    )}
                  </Heading>
                )}
                <table className={s.container}>
                  <thead>
                    <tr className={clsx(s.row, s.columnTitles)}>
                      {table?.columnTitles?.map(
                        ({ title, id }) =>
                          title && (
                            <Body
                              tag="td"
                              key={`${title}_${id}`}
                              weight="semiBold"
                            >
                              {title}
                            </Body>
                          ),
                      )}
                    </tr>
                  </thead>
                  <tbody className={s.body}>
                    {table?.columnRows?.length > 0 &&
                      table?.columnRows?.map((row, idx) => (
                        <tr
                          key={`row_${idx + 1}`}
                          className={clsx(s.row, {
                            [s.witoutLabelMobile]: isSmall,
                          })}
                        >
                          {row?.rowItem?.map((item, cellIdx) =>
                            item ? (
                              <Body
                                tag="td"
                                key={`row_${idx}_${table?.columnTitles?.[cellIdx]?.title ?? ""}_${item?.text ?? cellIdx}`}
                                data-label={
                                  table?.columnTitles?.[cellIdx]?.title ?? ""
                                }
                                weight={cellIdx === 0 ? "semiBold" : "regular"}
                              >
                                {item?.text ?? ""}
                              </Body>
                            ) : null,
                          )}
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            );
          })}
      </div>
      {moreBtn && (
        <ButtonWithIcon
          iconName={moreBtn?.ui?.pageIcon?.icon?.name || "plus"}
          iconFillColor={moreBtn?.ui?.pageIcon?.fillColor || "gold"}
          iconStrokeColor={moreBtn?.ui?.pageIcon?.strokeColor || "bistre"}
          iconSvgCode={moreBtn?.ui?.pageIcon?.uicon?.svg_code}
          variant="primary-dark"
          className={s.button}
          onClick={moreBtn?.onClick}
        >
          {moreBtn?.ui?.text ?? ""}
        </ButtonWithIcon>
      )}
    </section>
  );
};

PricingPlans.displayName = "PricingPlans";
